import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MapPin } from "lucide-react";

function loadGoogleMapsScript(callback) {
  if (window.google && window.google.maps && window.google.maps.places) {
    callback();
    return;
  }
  
  var existingScript = document.getElementById("google-maps-script");
  if (existingScript) {
    existingScript.addEventListener("load", callback);
    return;
  }
  
  var script = document.createElement("script");
  script.id = "google-maps-script";
  var apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "";
  script.src = "https://maps.googleapis.com/maps/api/js?key=" + apiKey + "&libraries=places&region=GB";
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
}

function parseAddressComponent(component, addressData) {
  var type = component.types[0];
  var value = component.long_name;
  
  if (type === "street_number") {
    addressData.streetNumber = value;
  } else if (type === "route") {
    addressData.streetName = value;
  } else if (type === "subpremise") {
    addressData.address_line_2 = value;
  } else if (type === "locality" || type === "postal_town") {
    addressData.city = value;
  } else if (type === "administrative_area_level_2") {
    addressData.county = value;
  } else if (type === "postal_code") {
    addressData.postcode = value;
  } else if (type === "country") {
    addressData.country = value;
  }
}

export default function AddressAutocomplete(props) {
  var prefix = props.prefix || "pickup";
  var label = props.label || "Address";
  var onAddressChange = props.onAddressChange;
  var initialData = props.initialData || {};

  var [isLoaded, setIsLoaded] = useState(false);
  var [isLoadError, setIsLoadError] = useState(false);
  var autocompleteRef = useRef(null);
  var inputRef = useRef(null);
  
  var [formData, setFormData] = useState({
    company_name: initialData.company_name || "",
    contact_name: initialData.contact_name || "",
    contact_phone: initialData.contact_phone || "",
    address_line_1: initialData.address_line_1 || "",
    address_line_2: initialData.address_line_2 || "",
    city: initialData.city || "",
    county: initialData.county || "",
    postcode: initialData.postcode || "",
    country: "United Kingdom"
  });
  
  var apiKey = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;
  var hasApiKey = apiKey && apiKey !== "" && apiKey.indexOf("PLACEHOLDER") === -1;

  useEffect(function() {
    if (!hasApiKey) {
      setIsLoadError(true);
      return;
    }
    loadGoogleMapsScript(function() {
      setIsLoaded(true);
    });
  }, [hasApiKey]);

  useEffect(function() {
    if (!isLoaded || !inputRef.current || !window.google) return;

    try {
      var autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        componentRestrictions: { country: "gb" },
        fields: ["address_components", "formatted_address", "geometry"],
        types: ["address"]
      });

      autocomplete.addListener("place_changed", function() {
        var place = autocomplete.getPlace();
        if (!place.address_components) return;

        var addressData = {
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          contact_phone: formData.contact_phone,
          address_line_1: "",
          address_line_2: "",
          city: "",
          county: "",
          postcode: "",
          country: "United Kingdom",
          streetNumber: "",
          streetName: ""
        };

        for (var i = 0; i < place.address_components.length; i++) {
          parseAddressComponent(place.address_components[i], addressData);
        }

        if (addressData.streetNumber) {
          addressData.address_line_1 = addressData.streetNumber + " " + addressData.streetName;
        } else {
          addressData.address_line_1 = addressData.streetName || "";
        }

        delete addressData.streetNumber;
        delete addressData.streetName;

        setFormData(addressData);
        if (onAddressChange) onAddressChange(addressData);
      });

      autocompleteRef.current = autocomplete;
    } catch (error) {
      console.error("Error initializing autocomplete:", error);
      setIsLoadError(true);
    }

    return function() {
      if (autocompleteRef.current && window.google && window.google.maps && window.google.maps.event) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [isLoaded, formData.company_name, formData.contact_name, formData.contact_phone, onAddressChange]);

  function handleFieldChange(field, value) {
    var newData = Object.assign({}, formData);
    newData[field] = value;
    setFormData(newData);
    if (onAddressChange) onAddressChange(newData);
  }

  var iconColor = prefix === "pickup" ? "text-[#22c55e]" : "text-[#3b82f6]";

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className={"w-5 h-5 " + iconColor} />
        <span className="text-lg font-bold text-white">{label}</span>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">
            Company Name (Optional)
          </Label>
          <Input
            value={formData.company_name}
            onChange={function(e) { handleFieldChange("company_name", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder="Company at this address"
            data-testid={prefix + "-company-name"}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">
            Contact Name *
          </Label>
          <Input
            value={formData.contact_name}
            onChange={function(e) { handleFieldChange("contact_name", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder="Person at this location"
            required
            data-testid={prefix + "-contact-name"}
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">
            Contact Phone *
          </Label>
          <Input
            value={formData.contact_phone}
            onChange={function(e) { handleFieldChange("contact_phone", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder="+44 7XXX XXXXXX"
            required
            data-testid={prefix + "-contact-phone"}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">
          Address Line 1 (Street + Number) *
          {isLoaded && (
            <span className="ml-2 text-[#22c55e] text-[10px]">Autocomplete Active</span>
          )}
        </Label>
        <div className="relative">
          <Input
            ref={inputRef}
            value={formData.address_line_1}
            onChange={function(e) { handleFieldChange("address_line_1", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder={hasApiKey ? "Start typing address..." : "Enter street address"}
            required
            data-testid={prefix + "-address-line-1"}
          />
          {!isLoaded && hasApiKey && !isLoadError && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 animate-spin" />
          )}
        </div>
        {isLoadError && (
          <p className="text-xs text-amber-400">
            Address autocomplete unavailable. Please enter address manually.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">
          Address Line 2 (Unit/Building - Optional)
        </Label>
        <Input
          value={formData.address_line_2}
          onChange={function(e) { handleFieldChange("address_line_2", e.target.value); }}
          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
          placeholder="Apartment, suite, unit, building, floor, etc."
          data-testid={prefix + "-address-line-2"}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">City *</Label>
          <Input
            value={formData.city}
            onChange={function(e) { handleFieldChange("city", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder="London"
            required
            data-testid={prefix + "-city"}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">County</Label>
          <Input
            value={formData.county}
            onChange={function(e) { handleFieldChange("county", e.target.value); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] rounded-lg h-12"
            placeholder="Greater London"
            data-testid={prefix + "-county"}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Postcode *</Label>
          <Input
            value={formData.postcode}
            onChange={function(e) { handleFieldChange("postcode", e.target.value.toUpperCase()); }}
            className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:ring-[#22c55e] focus:border-[#22c55e] uppercase rounded-lg h-12"
            placeholder="SW1A 2AA"
            required
            data-testid={prefix + "-postcode"}
          />
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-400 font-medium">Country</Label>
          <Input
            value={formData.country}
            disabled
            className="bg-white/[0.02] border-white/5 text-slate-500 rounded-lg h-12"
            data-testid={prefix + "-country"}
          />
        </div>
      </div>
    </div>
  );
}
